import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { formatStorageLimit } from '@/lib/subscriptions';
import { useSubscription } from '@/hooks/useSubscription';
import { documentUploadQueue, withUploadRetry } from '@/lib/uploadQueue';

const DEFAULT_ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const DEFAULT_ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];

interface HookOptions {
  allowedMimeTypes?: string[];
  allowedExtensions?: string[];
}

interface UploadOptions {
  file: File;
  name: string;
  category?: string;
  matter_id?: string;
  stage_id?: string;
  deal_id?: string;
  visibility?: string;
  bucket?: string;
  createDocumentRecord?: boolean;
  bypassStorageLimit?: boolean;
}

export function useFileUpload(maxSizeMB = 10, options?: HookOptions) {
  const { user } = useAuth();
  const { entitlements, currentPlan } = useSubscription();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const allowedExtensions = options?.allowedExtensions || DEFAULT_ALLOWED_EXTENSIONS;
  const allowedTypes = options?.allowedMimeTypes || DEFAULT_ALLOWED_TYPES;

  const validate = (file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !allowedExtensions.includes(ext)) {
      return `Only ${allowedExtensions.join(', ').toUpperCase()} files are allowed`;
    }
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext)) {
      return 'Invalid file type';
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `File size must be under ${maxSizeMB}MB`;
    }
    return null;
  };

  const upload = async (options: UploadOptions) => {
    if (!user) throw new Error('Not authenticated');

    const validationError = validate(options.file);
    if (validationError) {
      setError(validationError);
      throw new Error(validationError);
    }

    setIsUploading(true);
    setProgress(0);
    setError(null);

    try {
      return await documentUploadQueue.run(async () => {
        setProgress(5);

        if (!options.bypassStorageLimit && entitlements.storageLimitBytes !== null) {
          const bucketsToCheck = Array.from(new Set(['documents', 'properties', options.bucket || 'documents']));
          let currentUsageBytes = 0;

          for (const bucketName of bucketsToCheck) {
            const { data: bucketFiles, error: bucketError } = await supabase.storage
              .from(bucketName)
              .list(user.id, { limit: 1000 });

            if (bucketError) {
              continue;
            }

            currentUsageBytes += (bucketFiles || []).reduce((sum, file) => {
              const size = typeof file.metadata?.size === 'number' ? file.metadata.size : 0;
              return sum + size;
            }, 0);
          }

          if (currentUsageBytes + options.file.size > entitlements.storageLimitBytes) {
            const message = `${currentPlan.name} includes ${formatStorageLimit(entitlements.storageLimitBytes)}. Remove files or upgrade before uploading more.`;
            setError(message);
            throw new Error(message);
          }
        }

        const bucket = options.bucket || 'documents';
        const createDocumentRecord = options.createDocumentRecord ?? bucket === 'documents';
        const filePath = `${user.id}/${Date.now()}_${options.file.name}`;

        // Upload to storage
        setProgress(10);
        await withUploadRetry(
          async () => {
            const result = await supabase.storage
              .from(bucket)
              .upload(filePath, options.file);

            if (result.error) {
              throw result.error;
            }

            return result;
          },
          {
            onRetry: (attempt) => {
              setProgress(Math.min(35, 10 + attempt * 8));
            },
          },
        );

        setProgress(60);

        const fileExt = options.file.name.split('.').pop();
        const publicUrl = bucket !== 'documents'
          ? supabase.storage.from(bucket).getPublicUrl(filePath).data.publicUrl
          : null;

        let data = null;

        if (createDocumentRecord) {
          const { data: document, error: dbError } = await supabase
            .from('documents')
            .insert({
              name: options.name,
              file_path: filePath,
              file_type: fileExt || null,
              file_size: options.file.size,
              category: options.category || null,
              matter_id: options.matter_id || null,
              stage_id: options.stage_id || null,
              deal_id: options.deal_id || null,
              visibility: options.visibility || 'lawyer-only',
              uploaded_by: user.id,
            })
            .select()
            .single();
          if (dbError) throw dbError;
          data = document;
        }

        setProgress(90);

        // Send notifications if visible to others
        if (createDocumentRecord && options.visibility === 'all-parties' && options.matter_id) {
          const { data: matter } = await supabase
            .from('matters')
            .select('client_id, matter_number')
            .eq('id', options.matter_id)
            .single();
          if (matter?.client_id) {
            await supabase.functions.invoke('create-notification', {
              body: {
                user_id: matter.client_id,
                title: 'New Document Shared',
                message: `A new document "${options.name}" has been shared with you.`,
                type: 'document',
                link_to: `/client-transaction/${options.matter_id}`,
              },
            });
          }
        }

        setProgress(100);
        if (createDocumentRecord) {
          queryClient.invalidateQueries({ queryKey: ['documents'] });
        }
        if (bucket === 'properties') {
          queryClient.invalidateQueries({ queryKey: ['properties'] });
        }

        return {
          ...data,
          filePath,
          publicUrl,
        };
      });
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  return { upload, isUploading, progress, error, validate };
}
